import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Library,
  Plus,
  Save,
  Trash2,
  Upload,
  Video,
  Volume2,
} from "lucide-react";
import { ApiError, api, json } from "../api";
import { compressImage } from "../media";

type MediaKind =
  | "avatar"
  | "photo"
  | "video"
  | "video_poster"
  | "greeting_image"
  | "greeting_audio";

type AdminMediaItem = {
  id: string;
  kind: MediaKind;
  url: string | null;
  mime_type: string;
  bytes: number | null;
  duration_seconds: number | null;
  ordinal: number;
  active: boolean;
  preview_url: string | null;
  source: "profile" | "upload";
  status: "queued" | "processing" | "ready" | "failed";
  original_name: string | null;
  in_use: boolean;
};

const uploadLabels: Record<Exclude<MediaKind, "video_poster">, string> = {
  avatar: "上传头像 · 2MB",
  photo: "添加照片 · 3MB",
  video: "添加视频 · 20MB",
  greeting_image: "招呼图片 · 3MB",
  greeting_audio: "招呼语音 · 3MB",
};

function readableBytes(bytes: number | null) {
  if (!bytes) return "等待处理";
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function errorLabel(error: unknown) {
  if (!(error instanceof ApiError)) return "操作失败，请重试";
  const labels: Record<string, string> = {
    GREETING_MEDIA_LIMIT: "招呼素材最多 10 个",
    ACCOUNT_UPLOAD_LIMIT: "当前上传尚未完成，请稍候",
    GLOBAL_UPLOAD_LIMIT: "服务器正在处理其他上传，请稍候",
    FILE_TOO_LARGE: "文件超过上传上限",
    UNSUPPORTED_MEDIA_TYPE: "不支持这种文件格式",
    MEDIA_IN_GREETING_SEQUENCE: "该素材正在招呼序列中使用，请先从序列移除",
    MEDIA_STILL_SELECTED: "该素材仍在资料中使用，请先移出相册或视频展示",
  };
  return labels[error.code] ?? "操作失败，请重试";
}

export function AdminMediaLibrary({
  womanId,
  onProfileMediaChange,
}: {
  womanId: string;
  onProfileMediaChange: () => void;
}) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  const [uploadingKind, setUploadingKind] = useState<MediaKind | null>(null);
  const media = useQuery({
    queryKey: ["admin-media", womanId],
    queryFn: () =>
      api<{ items: AdminMediaItem[] }>(`/admin/women/${womanId}/media`),
    refetchInterval: (query) =>
      query.state.data?.items.some((item) =>
        ["queued", "processing"].includes(item.status),
      )
        ? 1000
        : false,
  });
  const items = media.data?.items ?? [];
  const refresh = async (profileChanged = false) => {
    await queryClient.invalidateQueries({
      queryKey: ["admin-media", womanId],
    });
    if (profileChanged) onProfileMediaChange();
  };
  const upload = async (kind: Exclude<MediaKind, "video_poster">, file: File) => {
    setUploadingKind(kind);
    setNotice(`正在上传 ${file.name}`);
    try {
      const compressed = file.type.startsWith("image/")
        ? await compressImage(
            file,
            kind === "avatar" ? 1024 : kind === "greeting_image" ? 1280 : 1600,
          )
        : file;
      const body = new FormData();
      body.append("file", compressed);
      const result = await api<{ upload: { id: string } }>(
        `/admin/women/${womanId}/uploads/${kind}`,
        { method: "POST", body },
      );
      setNotice("上传完成，等待服务器压缩");
      await refresh();
      for (let attempt = 0; attempt < 90; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const status = await api<{
          upload: {
            status: "queued" | "processing" | "ready" | "failed";
            output_bytes: number | null;
            failure_reason: string | null;
          };
        }>(`/admin/uploads/${result.upload.id}`);
        if (status.upload.status === "queued") {
          setNotice("正在排队压缩");
          continue;
        }
        if (status.upload.status === "processing") {
          setNotice("服务器正在压缩");
          continue;
        }
        if (status.upload.status === "ready") {
          setNotice(`上传成功 · ${readableBytes(status.upload.output_bytes)}`);
          await refresh(["avatar", "photo", "video"].includes(kind));
          return;
        }
        setNotice(`压缩失败：${status.upload.failure_reason ?? "未知原因"}`);
        await refresh();
        return;
      }
      setNotice("处理时间较长，可稍后回到媒体库查看");
    } catch (error) {
      setNotice(errorLabel(error));
    } finally {
      setUploadingKind(null);
    }
  };
  const remove = useMutation({
    mutationFn: (mediaId: string) =>
      api(`/admin/women/${womanId}/media/${mediaId}`, { method: "DELETE" }),
    onSuccess: async () => {
      setNotice("素材已移除");
      await refresh(true);
    },
    onError: (error) => setNotice(errorLabel(error)),
  });
  const reorder = useMutation({
    mutationFn: ({ kind, ids }: { kind: "photo" | "video"; ids: string[] }) =>
      api(
        `/admin/women/${womanId}/media/order`,
        json("PUT", { kind, ids }),
      ),
    onSuccess: async () => {
      setNotice("展示顺序已更新");
      await refresh(true);
    },
    onError: (error) => setNotice(errorLabel(error)),
  });
  const select = useMutation({
    mutationFn: ({ mediaId, active }: { mediaId: string; active: boolean }) =>
      api(
        `/admin/women/${womanId}/media/${mediaId}/active`,
        json("PATCH", { active }),
      ),
    onSuccess: async (_result, values) => {
      setNotice(values.active ? "素材已加入资料展示" : "素材已移出资料展示");
      await refresh(true);
    },
    onError: (error) => setNotice(errorLabel(error)),
  });
  const move = (
    group: AdminMediaItem[],
    index: number,
    direction: -1 | 1,
  ) => {
    const next = [...group];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const currentItem = next[index];
    const targetItem = next[target];
    if (!currentItem || !targetItem) return;
    next[index] = targetItem;
    next[target] = currentItem;
    reorder.mutate({
      kind: currentItem.kind as "photo" | "video",
      ids: next.map((item) => item.id),
    });
  };
  const ready = (kind: MediaKind) =>
    items
      .filter((item) => item.kind === kind && item.status === "ready")
      .sort((a, b) => a.ordinal - b.ordinal);
  const pending = (kind: MediaKind) =>
    items.filter((item) => item.kind === kind && item.status !== "ready");
  const avatars = [...ready("avatar"), ...pending("avatar")];
  const photos = ready("photo");
  const photoItems = [...photos, ...pending("photo")];
  const videos = ready("video");
  const videoItems = [...videos, ...pending("video")];
  const greetingUploads = items.filter((item) =>
    ["greeting_image", "greeting_audio"].includes(item.kind),
  );
  const greetingChoices = items.filter((item) =>
    ["photo", "video", "greeting_image", "greeting_audio"].includes(item.kind) && item.status === "ready",
  );
  const posters = ready("video_poster");

  return (
    <section className="media-workbench">
      <header className="media-workbench-title">
        <span>
          <Library />
        </span>
        <div>
          <h3>该女士的独立素材库</h3>
          <p>图片、视频、语音分别管理；只从当前女士素材中选择</p>
        </div>
      </header>
      <MediaSection
        title="头像"
        detail={`${ready("avatar").length}/1`}
        kind="avatar"
        items={avatars}
        upload={(file) => upload("avatar", file)}
        uploading={uploadingKind === "avatar"}
        onRemove={(item) => remove.mutate(item.id)}
      />
      <MediaSection
        title="图片素材 / 相册"
        detail={`${photos.filter((item) => item.active).length} 张已选 · 前 3 张普通会员可见`}
        kind="photo"
        items={photoItems}
        upload={(file) => upload("photo", file)}
        uploading={uploadingKind === "photo"}
        onRemove={(item) => remove.mutate(item.id)}
        onToggle={(item) => select.mutate({ mediaId: item.id, active: !item.active })}
        onMove={(index, direction) => move(photos, index, direction)}
      />
      <MediaSection
        title="视频素材"
        detail={`${videos.filter((item) => item.active).length} 个已选 · 第 1 个普通会员可看`}
        kind="video"
        items={videoItems}
        upload={(file) => upload("video", file)}
        uploading={uploadingKind === "video"}
        onRemove={(item) => remove.mutate(item.id)}
        onToggle={(item) => select.mutate({ mediaId: item.id, active: !item.active })}
        onMove={(index, direction) => move(videos, index, direction)}
        posters={posters}
      />
      <section className="media-shelf greeting-assets">
        <header>
          <div>
            <h4>招呼专用图片与语音素材</h4>
            <small>{greetingUploads.filter((item) => item.status === "ready").length}/10 · 资料照片和视频也可在下方直接选择</small>
          </div>
          <div className="media-upload-pair">
            <UploadButton
              label={uploadLabels.greeting_image}
              accept="image/*"
              busy={uploadingKind === "greeting_image"}
              onFile={(file) => upload("greeting_image", file)}
            />
            <UploadButton
              label={uploadLabels.greeting_audio}
              accept="audio/*"
              busy={uploadingKind === "greeting_audio"}
              icon="audio"
              onFile={(file) => upload("greeting_audio", file)}
            />
          </div>
        </header>
        <div className="media-library-grid">
          {greetingUploads.length === 0 && (
            <p className="media-empty">上传图片或语音后，会显示在这里。</p>
          )}
          {greetingUploads.map((item) => (
            <MediaCard
              key={item.id}
              item={item}
              onRemove={() => remove.mutate(item.id)}
            />
          ))}
        </div>
      </section>
      {notice && <p className="media-notice" role="status">{notice}</p>}
      <GreetingEditor
        womanId={womanId}
        media={greetingChoices}
        onSaved={() => refresh()}
      />
    </section>
  );
}

function MediaSection({
  title,
  detail,
  kind,
  items,
  upload,
  uploading,
  onRemove,
  onToggle,
  onMove,
  posters = [],
}: {
  title: string;
  detail: string;
  kind: "avatar" | "photo" | "video";
  items: AdminMediaItem[];
  upload: (file: File) => void;
  uploading: boolean;
  onRemove: (item: AdminMediaItem) => void;
  onToggle?: (item: AdminMediaItem) => void;
  onMove?: (index: number, direction: -1 | 1) => void;
  posters?: AdminMediaItem[] | undefined;
}) {
  const readyItems = items.filter((item) => item.status === "ready");
  return (
    <section className="media-shelf" data-media-kind={kind}>
      <header>
        <div>
          <h4>{title}</h4>
          <small>{detail}</small>
        </div>
        <UploadButton
          label={uploadLabels[kind]}
          accept={kind === "video" ? "video/*" : "image/*"}
          busy={uploading}
          icon={kind === "video" ? "video" : "image"}
          onFile={upload}
        />
      </header>
      <div className="media-library-grid">
        {items.length === 0 && (
          <p className="media-empty">还没有{title}，从右上角添加。</p>
        )}
        {items.map((item) => {
          const readyIndex = readyItems.findIndex((entry) => entry.id === item.id);
          return (
            <MediaCard
              key={item.id}
              item={item}
              poster={
                kind === "video"
                  ? posters.find((entry) => entry.ordinal === item.ordinal)
                  : undefined
              }
              onRemove={() => onRemove(item)}
              onToggle={onToggle ? () => onToggle(item) : undefined}
              onMove={
                onMove && item.status === "ready"
                  ? (direction) => onMove(readyIndex, direction)
                  : undefined
              }
              first={readyIndex === 0}
              last={readyIndex === readyItems.length - 1}
            />
          );
        })}
      </div>
    </section>
  );
}

function UploadButton({
  label,
  accept,
  busy,
  icon = "image",
  onFile,
}: {
  label: string;
  accept: string;
  busy: boolean;
  icon?: "image" | "video" | "audio";
  onFile: (file: File) => void;
}) {
  const Icon = icon === "video" ? Video : icon === "audio" ? Volume2 : ImagePlus;
  return (
    <label className={`media-upload ${busy ? "busy" : ""}`}>
      <Icon />
      <span>{busy ? "处理中…" : label}</span>
      <input
        type="file"
        accept={accept}
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          event.currentTarget.value = "";
        }}
      />
    </label>
  );
}

function MediaCard({
  item,
  poster,
  onRemove,
  onToggle,
  onMove,
  first = true,
  last = true,
}: {
  item: AdminMediaItem;
  poster?: AdminMediaItem | undefined;
  onRemove: () => void;
  onToggle?: (() => void) | undefined;
  onMove?: ((direction: -1 | 1) => void) | undefined;
  first?: boolean;
  last?: boolean;
}) {
  const ready = item.status === "ready" && item.url;
  const isImage = ["avatar", "photo", "greeting_image"].includes(item.kind);
  return (
    <article className={`media-card ${item.status}`} data-media-id={item.id} data-kind={item.kind}>
      <div className="media-preview">
        {ready && isImage && <img src={item.url!} alt="媒体预览" />}
        {ready && item.kind === "video" && (
          <video controls preload="metadata" poster={poster?.url ?? undefined} src={item.url!} />
        )}
        {ready && item.kind === "greeting_audio" && (
          <div className="audio-preview">
            <Volume2 />
            <audio controls preload="metadata" src={item.url!} />
          </div>
        )}
        {!ready && (
          <div className="media-processing">
            <Upload />
            <span>{item.status === "failed" ? "处理失败" : "压缩中"}</span>
          </div>
        )}
      </div>
      <div className="media-card-meta">
        <span>
          <b>{item.original_name || item.kind.replace("greeting_", "招呼")}</b>
          <small>{item.in_use ? "招呼序列正在使用" : readableBytes(item.bytes)}</small>
        </span>
        {ready && (
          <div className="media-card-actions">
            {onToggle && (
              <button
                className={`media-select-toggle ${item.active ? "active" : ""}`}
                aria-label={item.active ? "移出资料展示" : "加入资料展示"}
                title={item.active ? "移出资料展示" : "加入资料展示"}
                onClick={onToggle}
              >
                {item.active ? "已选" : "选用"}
              </button>
            )}
            {onMove && (
              <>
                <button aria-label="向前移动" disabled={first} onClick={() => onMove(-1)}><ChevronUp /></button>
                <button aria-label="向后移动" disabled={last} onClick={() => onMove(1)}><ChevronDown /></button>
              </>
            )}
            <button
              aria-label="移除素材"
              disabled={item.in_use}
              onClick={() => {
                if (window.confirm("确定从媒体库移除这个素材吗？")) onRemove();
              }}
            >
              <Trash2 />
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

type GreetingDraft = {
  ordinal: number;
  messageType: "text" | "image" | "audio" | "video";
  content: string;
  mediaId: string | null;
  accessLevel: "free" | "vip";
  delaySeconds: number;
  active: boolean;
};

function GreetingEditor({
  womanId,
  media,
  onSaved,
}: {
  womanId: string;
  media: AdminMediaItem[];
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<GreetingDraft[] | null>(null);
  const [notice, setNotice] = useState("");
  const query = useQuery({
    queryKey: ["greetings", womanId],
    queryFn: () =>
      api<{
        items: Array<{
          ordinal: number;
          message_type: "text" | "image" | "audio" | "video";
          content: string;
          media_id: string | null;
          access_level: "free" | "vip";
          delay_seconds: number;
          active: boolean;
        }>;
      }>(`/admin/women/${womanId}/greetings`),
  });
  const items =
    draft ??
    query.data?.items.map((item) => ({
      ordinal: item.ordinal,
      messageType: item.message_type,
      content: item.content,
      mediaId: item.media_id,
      accessLevel: item.access_level,
      delaySeconds: item.delay_seconds,
      active: item.active,
    })) ??
    [];
  const mediaFor = (type: "image" | "audio" | "video") =>
    media.filter((item) =>
      type === "image"
        ? ["greeting_image", "photo"].includes(item.kind)
        : type === "audio"
          ? item.kind === "greeting_audio"
          : item.kind === "video",
    );
  const update = (index: number, values: Partial<GreetingDraft>) =>
    setDraft(
      items.map((item, currentIndex) =>
        currentIndex === index ? { ...item, ...values } : item,
      ),
    );
  const save = useMutation({
    mutationFn: () =>
      api(`/admin/women/${womanId}/greetings`, json("PUT", { items })),
    onSuccess: async () => {
      setDraft(null);
      setNotice("招呼序列已保存");
      await queryClient.invalidateQueries({ queryKey: ["greetings", womanId] });
      onSaved();
    },
    onError: (error) => setNotice(errorLabel(error)),
  });
  const incomplete = items.some((item) => !item.content.trim());
  return (
    <section className="greeting-editor media-greeting-editor">
      <div>
        <span>
          <h3>自动招呼序列</h3>
          <small>图片和语音从上方媒体库直接选择</small>
        </span>
        <button
          onClick={() =>
            setDraft([
              ...items,
              {
                ordinal: items.length,
                messageType: "text",
                content: "",
                mediaId: null,
                accessLevel: "free",
                delaySeconds: 1,
                active: true,
              },
            ])
          }
        >
          <Plus /> 添加一条
        </button>
      </div>
      {items.map((item, index) => {
        const choices =
          item.messageType === "text" ? [] : mediaFor(item.messageType);
        const selected = choices.find((choice) => choice.id === item.mediaId || choice.url === item.content);
        return (
          <article key={`${item.ordinal}-${index}`}>
            <select
              aria-label={`第 ${index + 1} 条招呼类型`}
              value={item.messageType}
              onChange={(event) => {
                const messageType = event.target.value as GreetingDraft["messageType"];
                update(index, {
                  messageType,
                  content:
                    messageType === "text"
                      ? ""
                      : mediaFor(messageType)[0]?.url ?? "",
                  mediaId: messageType === "text" ? null : mediaFor(messageType)[0]?.id ?? null,
                  accessLevel: messageType === "text" ? "free" : item.accessLevel,
                });
              }}
            >
              <option value="text">文字</option>
              <option value="image">媒体库图片</option>
              <option value="audio">媒体库语音</option>
              <option value="video">资料库视频</option>
            </select>
            {item.messageType === "text" ? (
              <input
                aria-label={`第 ${index + 1} 条招呼内容`}
                value={item.content}
                onChange={(event) => update(index, { content: event.target.value })}
                placeholder="输入招呼内容；可用 @位置 自动显示男用户IP城市"
              />
            ) : (
              <div className="greeting-media-picker">
                <select
                  aria-label={`第 ${index + 1} 条招呼媒体`}
                  value={selected?.id ?? ""}
                  onChange={(event) => {
                    const choice = choices.find((entry) => entry.id === event.target.value);
                    update(index, { mediaId: choice?.id ?? null, content: choice?.url ?? "" });
                  }}
                >
                  {!selected && <option value="">请选择当前女性的素材</option>}
                  {choices.map((choice, choiceIndex) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.original_name || `${item.messageType === "image" ? "图片" : item.messageType === "video" ? "视频" : "语音"} ${choiceIndex + 1}`}
                    </option>
                  ))}
                </select>
                {selected?.kind === "greeting_image" && selected.url && (
                  <img src={selected.url} alt="已选招呼图片" />
                )}
                {selected?.kind === "greeting_audio" && selected.url && (
                  <audio controls preload="metadata" src={selected.url} />
                )}
                {selected?.kind === "video" && selected.url && (
                  <video controls preload="metadata" playsInline src={selected.url} />
                )}
              </div>
            )}
            {item.messageType !== "text" && (
              <label className="greeting-access">
                观看权限
                <select value={item.accessLevel} onChange={(event) => update(index, { accessLevel: event.target.value as "free" | "vip" })}>
                  <option value="free">直接可看</option>
                  <option value="vip">VIP可看</option>
                </select>
              </label>
            )}
            <label>
              延迟
              <input
                aria-label={`第 ${index + 1} 条延迟秒数`}
                type="number"
                min="0"
                value={item.delaySeconds}
                onChange={(event) =>
                  update(index, { delaySeconds: Number(event.target.value) })
                }
              />
              秒
            </label>
            <button
              aria-label={`删除第 ${index + 1} 条招呼`}
              onClick={() =>
                setDraft(
                  items
                    .filter((_, currentIndex) => currentIndex !== index)
                    .map((entry, ordinal) => ({ ...entry, ordinal })),
                )
              }
            >
              ×
            </button>
          </article>
        );
      })}
      <button
        className="save-greetings"
        disabled={save.isPending || incomplete}
        onClick={() => save.mutate()}
      >
        <Save /> 保存招呼序列
      </button>
      {notice && <p className="greeting-notice" role="status">{notice}</p>}
    </section>
  );
}
